import { Button, LoadingSpinner, RentModeMapperPill, RowSpacer } from '../atoms';
import contractIcon from '@/assets/property-icons/neon-contract-icon.svg'
import doorIcon from '@/assets/property-icons/neon-door-icon.svg'
import checkIcon from '@/assets/property-icons/neon-check-icon.svg'
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import {
  useAddContactFormModalContext,
  useAddInsuranceFormModalContext,
  usePropertyDetailContext,
  useShowGalleryModalContext,
  useUserLanguage,
  useErrorHandlerContext,
} from '@/customHooks';
import { PropertyCommonsProps, PropertyDetail } from '@/custom-types';
import {
  ContactOutput,
  ContractStatusOption,
  IncidentStatusOption,
  UnityStatusOption,
} from '@/graphql/generated';
import textsData from '@/textData/PropertyDetailTexts.json'
import { capitalizeFirstLetter } from '@/helpers';
import { AddContactFormModal, AddInsuranceFormModal, ShowGalleryPictureModal } from '../templates';
import { PropertyCommonsInfoCard } from '../molecules';
import { useEffect, useRef, useState } from 'react';

type UnityType = NonNullable<PropertyDetail['unities']>[number]
//type IncidentType = NonNullable<PropertyDetail['incidents']>[number]

type SmallInfoCardProps = {
  title: string,
  value: string,
  iconSrc: string,
  onClick?: () => void
}
const SmallInfoCard = ({ title, value, iconSrc, onClick }: SmallInfoCardProps) => {
  return (
    <div
      className='flex cursor-pointer flex-col items-center rounded-2xl bg-white p-10 text-center shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
      onClick={() => onClick?.()}
    >
      <img src={iconSrc} width={64} alt='' />
      <h2 className='mb-2 text-xl'>
        {title}
      </h2>
      <div className='text-2xl font-bold text-[#00356b]'>
        {value}
      </div>
    </div>
  )
}


const AmountCardsData: CollectedAmountInfoCardProps[] = [
  {
    title: 'Year',
    amount: 0,
  },
  {
    title: 'Total',
    amount: 0,
  }
]

/* Colected Amount Card Component **************/
type CollectedAmountInfoCardProps = { title: string, amount: number }

const CollectedAmountInfoCard = ({ title, amount }: CollectedAmountInfoCardProps) => {
  return (
    <div className='rounded-2xl bg-white p-4 shadow-[0_4px_12px_rgba(0,0,0,0.08)]'>
      <p className='text-sm text-[#666]'>Collect Money by</p>
      <div className='mt-2 flex items-center justify-between gap-4'>
        <h2 className='text-xl'>{title}</h2>
        <div className='text-2xl font-bold text-[#00356b]'>{`$ ${amount}`}</div>
      </div>
    </div>
  )
}

/* Contact List Component **********************/
type ContactListProps = {
  contacts: ContactOutput[] | undefined
}

const ContactList = (props: ContactListProps) => {
  if (!props.contacts || props.contacts.length === 0) return (
    <div className='max-h-[180px] overflow-x-auto'>
      <p>Noy contactos ceados aún, puede crear nuevos contactos</p>
    </div>
  )

  return (
    <div className='grid max-h-[180px] gap-6 overflow-x-auto md:grid-cols-3'>
      {
        props.contacts.map((contact) => (
          <div
            key={contact.id}
            className='mb-4 flex min-w-[250px] flex-col items-start rounded-2xl bg-white p-4 shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
          >
            <h2 className='mb-2 text-xl'>{`${capitalizeFirstLetter(contact.firstName)} ${capitalizeFirstLetter(contact.lastName)}`}</h2>
            <p className='text-sm text-[#666]'><b>Phone&nbsp;:&nbsp;</b>{contact.phoneNumber}</p>
            <p className='text-sm text-[#666]'><b>Email&nbsp;:&nbsp;</b>{contact.email}</p>
          </div>
        ))
      }
    </div>
  )
}

/* Insurances List component ***************************/
type InsuranceRow = {
  id: string
  insuranceCompany: string
  insurancePhone: string
  policyNumber: string
  insuranceHolder: string
  holderDocument: string
  insuranceBroker: string
  insuranceBrokerPhone: string
  insuranceBrokerEmail: string
  policyPlan: string
}

type InsurancesListProps = {
  insurances: InsuranceRow[] | undefined
  texts: {
    noData: string
    headers: {
      company: string
      phone: string
      policyNumber: string
      holder: string
      holderDocument: string
      broker: string
      brokerPhone: string
      brokerEmail: string
      policyPlan: string
    }
  }
}

const InsurancesList = (props: InsurancesListProps) => {
  if (!props.insurances || props.insurances.length === 0) return (
    <p>{props.texts.noData}</p>
  )
  return (
    <table className='w-full border-collapse'>
      <thead>
        <tr>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.company}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.phone}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.policyNumber}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.holder}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.holderDocument}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.broker}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.brokerPhone}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.brokerEmail}</th>
          <th className='border-b border-[#eee] p-3 text-left'>{props.texts.headers.policyPlan}</th>
        </tr>
      </thead>
      <tbody>
        {props.insurances.map((insurance) => (
          <tr key={insurance.id}>
            <td className='border-b border-[#eee] p-3'>{insurance.insuranceCompany}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.insurancePhone}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.policyNumber}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.insuranceHolder}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.holderDocument}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.insuranceBroker}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.insuranceBrokerPhone}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.insuranceBrokerEmail}</td>
            <td className='border-b border-[#eee] p-3'>{insurance.policyPlan}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/*****************************************************/

export const PropertyDetailOverview = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.overview : textsData.en.overview

  const { property } = usePropertyDetailContext()
  const { openModal: showPictureGallery } = useShowGalleryModalContext()
  const { error: errorHandle } = useErrorHandlerContext()

  const [isGalleryLoading, setIsGalleryLoading] = useState(false)
  const [downloadedImages, setDownloadedImages] = useState<File[]>([])
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const picturesRowRef = useRef<HTMLDivElement | null>(null)

  const unities = property?.unities ?? []

  const commons: PropertyCommonsProps = {
    balcony: property?.balcony ?? false,
    patio: property?.patio ?? false,
    commonBathroom: property?.commonBathroom ?? false,
    kitchen: property?.kitchen ?? false,
    elevator: property?.elevator ?? false,
    commonAirConditioner: property?.commonAirConditioner ?? false,
    commonFan: property?.commonFan ?? false,
  }

  const { openModal: openInsuranceModal } = useAddInsuranceFormModalContext()
  const { openModal: openContactModal } = useAddContactFormModalContext()

  const incidents = (() => {
    if (!property) return 0
    const activeIncidents = property.incidents.filter((incident) => incident.statusOption !== IncidentStatusOption.Cancelled)

    return activeIncidents.length;
  })()

  const contracts = (() => {
    if (!property) return 0
    const activeContracts = property.unities.flatMap((unity) => unity.contracts);

    return activeContracts.length;
  })()

  const canceledContracts = (() => {
    if (!property) return 0
    const contracts = property.unities.flatMap((unity) => unity.contracts);
    const canceledContracts = contracts.filter((contract) => contract.statusOption === ContractStatusOption.Canceled)

    return canceledContracts.length;
  })()

  const contacts = ((): ContactListProps['contacts'] | undefined => {
    if (!property?.contacts) return undefined
    return property.contacts.map((contact) => ({
      id: contact.id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      phoneNumber: contact.phoneNumber,
      email: contact.email,
    }))
  })()

  const insurances = ((): InsurancesListProps['insurances'] | undefined => {
    if (!property?.insurances) return undefined
    return property.insurances.map((insurance: any) => ({
      id: insurance.id,
      insuranceCompany: insurance.insuranceCompany ?? '',
      insurancePhone: insurance.insurancePhone ?? '',
      policyNumber: insurance.policyNumber ?? '',
      insuranceHolder: insurance.insuranceHolder ?? '',
      holderDocument: insurance.holderDocument ?? '',
      insuranceBroker: insurance.insuranceBroker ?? '',
      insuranceBrokerPhone: insurance.insuranceBrokerPhone ?? '',
      insuranceBrokerEmail: insurance.insuranceBrokerEmail ?? '',
      policyPlan: insurance.policyPlan ?? '',
    }))
  })()

  const ocupancy = (unities: UnityType[]): string => {
    const occupied = unities.filter((unity) => unity?.currentStatusOption === UnityStatusOption.Occupied)

    return `${occupied.length.toString()} / ${unities.length.toString()}`
  }

  const navigate = useNavigate()

  const downloadImages = async () => {
    if (!property?.fileUploads || property.fileUploads.length === 0 || downloadedImages.length > 0) {
      return
    }

    try {
      setIsGalleryLoading(true)
      const mediaPromises = property.fileUploads
        .filter(fileUpload => (fileUpload.fileTypeOption === 'PICTURE' || fileUpload.fileTypeOption === 'VIDEO') && fileUpload.urlToDownload)
        .map(async (fileUpload) => {
          const url = fileUpload.urlToDownload as string
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Error al descargar el archivo: ${url}`)
          }
          const blob = await response.blob()
          const filename = url.substring(url.lastIndexOf('/') + 1).split('?')[0]
          const mimeType = fileUpload.fileTypeOption === 'VIDEO' ? 'video/mp4' : blob.type
          return new File([blob], filename, { type: mimeType })
        })

      const files = await Promise.all(mediaPromises)
      setDownloadedImages(files)
    } catch (error) {
      errorHandle(error)
    } finally {
      setIsGalleryLoading(false)
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          downloadImages()
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (picturesRowRef.current) {
      observer.observe(picturesRowRef.current)
    }

    return () => {
      if (picturesRowRef.current) {
        observer.unobserve(picturesRowRef.current)
      }
    }
  }, [property?.fileUploads])

  useEffect(() => {
    if (downloadedImages.length === 0) return

    const urls = downloadedImages.map(file => URL.createObjectURL(file))
    setImageUrls(urls)

    return () => {
      urls.forEach(url => URL.revokeObjectURL(url))
    }
  }, [downloadedImages])

  const openGalleryHandle = async () => {
    if (downloadedImages.length === 0) {
      console.log('No hay imágenes para mostrar.')
      return
    }
    showPictureGallery(downloadedImages)
  }

  const smallInfoCardData: SmallInfoCardProps[] = [
    {
      title: texts.minicards.card1,
      value: contracts.toString(),
      iconSrc: contractIcon,
    },
    {
      title: texts.minicards.card3,
      value: canceledContracts.toString(),
      iconSrc: checkIcon,
    },
    {
      title: texts.minicards.card2,
      value: incidents.toString(),
      iconSrc: doorIcon,
    },
    {
      title: texts.minicards.card4,
      value: ocupancy(unities),
      iconSrc: checkIcon,
    },
  ]

  return (
    <>
      <div className='p-6'>
        <div className='flex flex-col justify-between gap-4 lg:flex-row lg:items-center'>
          <div>
            <h1>{texts.title}</h1>
            <div className='mt-2 flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center'>
              <p className='text-sm text-[#666]'><b>{texts.id}</b> {property?.id}</p>
              <div className='flex flex-wrap items-center gap-2'>
                <p className='text-sm text-[#666]'><b>{texts.rentMode}</b></p>
                <RentModeMapperPill isForRentWhole={!!property?.isForRentWhole} />
              </div>
            </div>
          </div>
          <div className='flex w-full justify-start lg:w-auto lg:justify-end'>
            <Button onClick={() => navigate(`/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.CREATE}`)}>
              + Add Contract
            </Button>
          </div>
        </div>
        <div>
          <h3 className='my-6 text-[1.1rem]'>{texts.sectionsTitles.section1}</h3>
          <div className='mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-4'>
            {
              smallInfoCardData.map((data, index) => (
                <SmallInfoCard
                  key={data.title + index}
                  title={data.title}
                  value={data.value}
                  iconSrc={data.iconSrc}
                />
              ))
            }
          </div>
        </div>
        <RowSpacer height={3} />
        <div>
          <h3 className='my-6 text-[1.1rem]'>{texts.sectionsTitles.section2}</h3>
          <PropertyCommonsInfoCard data={commons} />
        </div>
        <RowSpacer height={3} />
        <div>
          <h3 className='my-6 text-[1.1rem]'>{texts.sectionsTitles.section3}</h3>
          <div className='mt-6 grid gap-6 sm:grid-cols-2'>
            {
              AmountCardsData.map((data, index) => (
                <CollectedAmountInfoCard
                  key={data.title + index}
                  title={data.title}
                  amount={data.amount}
                />
              ))
            }
          </div>
        </div>
        <RowSpacer height={3} />
        <div className='flex items-center justify-between gap-4'>
          <h3 className='my-6 text-[1.1rem]'>{texts.sectionsTitles.section4}</h3>
          {imageUrls.length > 0 && (
            <Button
              onClick={() => openGalleryHandle()}
              isLoading={isGalleryLoading}
            >
              {texts.pictureGallerySection?.button2}
            </Button>
          )}
        </div>
        <br />
        {
          isGalleryLoading
            ? (
              <div className='flex justify-center'>
                <LoadingSpinner />
              </div>
            )
            : (
              <div ref={picturesRowRef}>
                <div className='grid gap-6 lg:grid-cols-4'>
                  {
                    imageUrls.length > 0
                      ? (
                        imageUrls.slice(-4).map((url, index) => {
                          const originalIndex = imageUrls.length >= 4 ? imageUrls.length - 4 + index : index
                          const file = downloadedImages[originalIndex]
                          const isVideo = file?.type?.startsWith('video/')
                          return (
                            <div key={index} className='relative aspect-[3/2] overflow-hidden rounded-md bg-black shadow-[0_5px_5px_rgba(0,0,0,0.2)]'>
                                {isVideo ? (
                                  <div className='relative h-full w-full'>
                                    <video
                                      className='h-full w-full object-cover'
                                      src={url}
                                      muted
                                      preload="auto"
                                      playsInline
                                    />
                                    <div aria-hidden className='pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25'>
                                      <div
                                        className='ml-1 h-0 w-0 border-y-[20px] border-l-[36px] border-y-transparent border-l-white/95'
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <img className='h-full w-full object-cover' src={url} alt={`thumbnail ${index}`} />
                                )}
                            </div>
                          )
                        })
                      )
                      : property?.fileUploads && property.fileUploads.length > 0
                        ? null
                        : (
                          <p>{texts.pictureGallerySection?.noImages}</p>
                        )
                  }
                </div>
              </div>
            )
        }
        <RowSpacer height={3} />
        <div className='flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center'>
          <h3 className='my-6 text-[1.1rem]'>{texts.sectionsTitles.section5}</h3>
          <div>
            <Button
              secondary
              onClick={() => openContactModal()}
            >+ Agregar Contacto</Button>
          </div>
        </div>
        <ContactList contacts={contacts} />
        <RowSpacer height={3} />
        <div className='flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center'>
          <h3 className='my-6 text-[1.1rem]'>{texts.sectionsTitles.section6}</h3>
          <div>
            <Button
              secondary
              onClick={() => openInsuranceModal()}
            >+ Agregar Póliza</Button>
          </div>
        </div>
        <InsurancesList
          insurances={insurances}
          texts={texts.insurancesTable}
        />
      </div>
      <AddInsuranceFormModal />
      <AddContactFormModal />
      <ShowGalleryPictureModal />
    </>
  );
};
